import { ClerkApp, useOrganization, useUser } from "@clerk/remix";
import { getAuth, rootAuthLoader } from "@clerk/remix/ssr.server";
import { datadogRum } from "@datadog/browser-rum";
import type { LinksFunction, LoaderFunctionArgs } from "@remix-run/node";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLoaderData,
  useNavigation,
  useRouteError,
} from "@remix-run/react";
import { featureFlagSvc } from "@repo/services/featureFlag";
import { identitySvc } from "@repo/services/identity";
import {
  cn,
  FeatureFlagsContext,
  LogoBlueOnWhite,
  PrimaryFullScreenSpinner,
  PrimaryUIProvider,
  TooltipProvider,
  UnhandledAppError,
} from "@repo/ui";
import { createLogger } from "@repo/utils";
import riveWasmUrl from "@rive-app/canvas/rive.wasm";
import tracer from "dd-trace";
import * as React from "react";
import { Suspense, useEffect } from "react";
import { ClientOnly } from "remix-utils/client-only";
import { match, P } from "ts-pattern";
import logoBlueOnWhite from "~/assets/brand/logoBlueOnWhite.svg";
import interFont from "~/fonts/inter/inter.css?url";
import globalsStylesheetUrl from "~/styles/globals.css?url";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: globalsStylesheetUrl },
  { rel: "stylesheet", href: interFont },
];

const logger = createLogger({
  name: "app.root",
});

export const loader = async (args: LoaderFunctionArgs) => {
  return await tracer.trace("app.root", async () => {
    return rootAuthLoader(args, async (_postClerkArgs) => {
      let featureFlags = new Map<string, boolean>();
      const auth = await getAuth(args);
      const identity = await identitySvc.createIdentityFromClerkAuth(auth);

      if (identity) {
        identitySvc.traceIdentity(identity);
        featureFlags = await featureFlagSvc.evaluateFeatureFlags({
          context: { identity },
        });
      }
      return { featureFlags: Object.fromEntries(featureFlags) };
    });
  });
};

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <title>Primary</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
        <link
          rel="preload"
          href={riveWasmUrl}
          as="fetch"
          crossOrigin="anonymous"
        />
        <Meta />
        <Links />
      </head>
      <body className="h-dvh overflow-hidden">
        <TooltipProvider>{children}</TooltipProvider>

        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function GlobalLoading() {
  const navigation = useNavigation();
  const active = navigation.state !== "idle";

  const ref = React.useRef<HTMLDivElement>(null);
  const [animating, setAnimating] = React.useState(false);

  React.useEffect(() => {
    if (!ref.current) return;
    if (active) setAnimating(true);

    Promise.allSettled(
      ref.current.getAnimations().map(({ finished }) => finished),
    )
      .then(() => !active && setAnimating(false))
      .catch(logger.error);
  }, [active]);

  return (
    <div
      role="progressbar"
      aria-hidden={!active}
      aria-valuetext={active ? "Loading" : undefined}
      className="fixed inset-x-0 top-0 z-global-loading h-0.5 animate-pulse"
    >
      <div
        ref={ref}
        className={cn(
          "h-full bg-gradient-to-r from-brand-200 to-brand-500 transition-all duration-500 ease-in-out",
          match([navigation.state, animating])
            .with(["idle", false], () => "w-0 opacity-0 transition-none")
            .with(["submitting", P._], () => "w-5/12")
            .with(["loading", P._], () => "w-10/12")
            .otherwise(() => "w-full"),
        )}
      />
    </div>
  );
}

function AppWithProviders() {
  const data = useLoaderData<typeof loader>();

  const user = useUser();
  const { organization } = useOrganization();

  useEffect(() => {
    if (user.user) {
      datadogRum.setUser({
        id: user.user.id,
      });
    }
  }, [user.user, user.user?.id]);

  useEffect(() => {
    if (organization?.id && organization.id !== "") {
      datadogRum.setUserProperty("organisation_id", organization.id);
    }
    if (organization?.name && organization.name !== "") {
      datadogRum.setUserProperty("organisation_name", organization.name);
    }
  }, [organization, organization?.id, organization?.name]);

  return (
    <ClientOnly>
      {() => (
        <Suspense fallback={<PrimaryFullScreenSpinner />}>
          <FeatureFlagsContext.Provider value={data.featureFlags}>
            <PrimaryUIProvider>
              <Outlet />
            </PrimaryUIProvider>
            <GlobalLoading />
          </FeatureFlagsContext.Provider>
        </Suspense>
      )}
    </ClientOnly>
  );
}

export function RootErrorBoundary() {
  const error = useRouteError();

  logger.error({ error });

  return (
    <UnhandledAppError
      Container={({ children }) => <div className="p-8">{children}</div>}
      logo={<LogoBlueOnWhite src={logoBlueOnWhite} />}
    />
  );
}

export default ClerkApp(AppWithProviders);

export const ErrorBoundary = RootErrorBoundary;
